import {
  IcoChevronDown,
  IcoCursor,
  IcoSwatch,
  IcoImage,
  IcoRuzukuLogo,
  IcoX,
  IcoCube,
  IcoRectangleGroup,
} from '@components/icons';
import { Brand } from './load';
import { pickLogo } from './pick-logo';
import { SettingsTab } from './settings-tab';
import { StyledColorPicker } from './styled-color-picker';
import { EditMode } from './types';
import { Button } from '@components/buttons';
import { Toggle } from '@components/toggle';
import { themeDefaults } from 'shared/tenant-style-utils';
import { Slider } from '@components/slider';
import { SettingsButton } from './settings-button';

export type SettingsPanelProps = {
  editMode: EditMode | undefined;
  setEditMode: (mode: EditMode | undefined) => void;
  brand: Brand;
  setBrand: (fn: (b: Brand) => Brand) => void;
};

function BackgroundPane({ setEditMode, setBrand, brand }: SettingsPanelProps) {
  return (
    <SettingsTab key="background" title="Background Settings" onBack={() => setEditMode(undefined)}>
      <div class="flex flex-col gap-4">
        <div>
          <StyledColorPicker
            name="backgroundColor"
            color={brand.backgroundColor}
            onPick={(backgroundColor) => {
              setBrand((s) => ({ ...s, backgroundColor }));
            }}
          >
            Background Color
          </StyledColorPicker>
        </div>
        <button
          type="button"
          class="p-3 text-left rounded-lg border hover:bg-gray-50 flex items-center justify-between"
          onClick={async () => {
            const loginBg = await pickLogo({ cropRatio: undefined, requireCrop: false });
            loginBg && setBrand((s) => ({ ...s, loginBgUrl: loginBg.publicUrl }));
          }}
        >
          <span class="flex items-center gap-2">
            {!!brand.loginBgUrl && (
              <>
                <img src={brand.loginBgUrl} class="max-w-80 h-10" />
                <span>Change</span>
              </>
            )}
            {!brand.loginBgUrl && (
              <>
                <IcoImage class="size-6 opacity-75" />
                <span>Choose background image</span>
              </>
            )}
          </span>
          <IcoChevronDown class="w-4 h-4 opacity-75" />
        </button>
        {!!brand.loginBgUrl && (
          <button
            type="button"
            class="p-3 text-left rounded-lg border hover:bg-red-50 hover:border-red-200 hover:text-red-600 flex items-center justify-between"
            onClick={() => setBrand((s) => ({ ...s, loginBgUrl: '' }))}
          >
            <span class="flex items-center gap-2">
              <IcoX class="size-6 opacity-75" />
              <span>Remove background image</span>
            </span>
          </button>
        )}
      </div>
    </SettingsTab>
  );
}

function LogoPane({ setEditMode, setBrand, brand }: SettingsPanelProps) {
  return (
    <SettingsTab key="logo" title="Logo Settings" onBack={() => setEditMode(undefined)}>
      <div class="flex flex-col gap-4">
        <Button
          type="button"
          class="cursor-pointer focus:outline-hidden focus:ring-2 focus:ring-indigo-600 flex items-center justify-center rounded-lg px-8 relative"
          onClick={async () => {
            const icon = await pickLogo();
            icon && setBrand((s) => ({ ...s, iconUrl: icon.publicUrl }));
          }}
        >
          <span class="inline-block h-16">
            {!!brand.iconUrl && <img src={brand.iconUrl} class="h-full" />}
            {!brand.iconUrl && <IcoRuzukuLogo class="h-full w-auto" />}
          </span>
          <IcoChevronDown class="w-4 h-4 ml-2 opacity-75 absolute right-2" />
        </Button>
        <div>
          <label class="gap-3 text-gray-500 cursor-pointer flex items-center">
            <Toggle
              checked={brand.showName}
              onClick={() => setBrand((s) => ({ ...s, showName: !s.showName }))}
            />
            <span>Show brand name</span>
          </label>
          {brand.showName && (
            <div class="mt-4">
              <StyledColorPicker
                name="logo"
                color={brand.logoColor || themeDefaults.logoColor}
                onPick={(logoColor) => setBrand((s) => ({ ...s, logoColor }))}
              >
                Brand name color
              </StyledColorPicker>
            </div>
          )}
        </div>
      </div>
    </SettingsTab>
  );
}

function GradientPane({ setEditMode, setBrand, brand }: SettingsPanelProps) {
  return (
    <SettingsTab key="gradient" title="Background Accent" onBack={() => setEditMode(undefined)}>
      <div class="flex flex-col gap-4">
        <label class="gap-3 text-gray-500 cursor-pointer flex items-center">
          <Toggle
            checked={brand.enableGradient}
            onClick={() => setBrand((b) => ({ ...b, enableGradient: !b.enableGradient }))}
          />
          <span>Show angled background</span>
        </label>
        {brand.enableGradient && (
          <>
            <StyledColorPicker
              name="fromAccent"
              color={brand.fromAccentColor || themeDefaults.fromAccentColor}
              onPick={(fromAccentColor) => setBrand((s) => ({ ...s, fromAccentColor }))}
            >
              Start color
            </StyledColorPicker>
            <StyledColorPicker
              name="toAccent"
              color={brand.toAccentColor || themeDefaults.toAccentColor}
              onPick={(toAccentColor) => setBrand((s) => ({ ...s, toAccentColor }))}
            >
              End color
            </StyledColorPicker>
          </>
        )}
      </div>
    </SettingsTab>
  );
}

function ButtonsPane({ setEditMode, setBrand, brand }: SettingsPanelProps) {
  return (
    <SettingsTab key="buttons" title="Button & Link Settings" onBack={() => setEditMode(undefined)}>
      <div class="flex flex-col gap-4">
        <StyledColorPicker
          name="primaryAction"
          color={brand.primaryActionColor || '#4F46E5'}
          onPick={(primaryActionColor) => setBrand((s) => ({ ...s, primaryActionColor }))}
        >
          Primary button & link color
        </StyledColorPicker>
        <div class="flex flex-col gap-2">
          <Slider
            label="Button rounding"
            value={brand.buttonBorderRadius}
            min={0}
            max={32}
            minLabel="Square"
            maxLabel="Rounded"
            onChange={(value) => setBrand((b) => ({ ...b, buttonBorderRadius: value }))}
          />
        </div>
      </div>
    </SettingsTab>
  );
}

function ContainerPane({ setEditMode, setBrand, brand }: SettingsPanelProps) {
  return (
    <SettingsTab key="container" title="Container Settings" onBack={() => setEditMode(undefined)}>
      <div class="flex flex-col gap-16">
        <div class="flex flex-col gap-2">
          <Slider
            label="Padding"
            value={brand.containerPadding}
            min={16}
            max={64}
            minLabel="Compact"
            maxLabel="Spacious"
            onChange={(value) => setBrand((b) => ({ ...b, containerPadding: value }))}
          />
        </div>
        <div class="flex flex-col gap-2">
          <Slider
            label="Rounding"
            value={brand.containerBorderRadius}
            min={0}
            max={32}
            minLabel="Square"
            maxLabel="Rounded"
            onChange={(value) => setBrand((b) => ({ ...b, containerBorderRadius: value }))}
          />
        </div>
        <div class="flex flex-col gap-2">
          <Slider
            label="Shadow"
            value={brand.containerShadow}
            min={0}
            max={400}
            minLabel="None"
            maxLabel="Heavy"
            onChange={(value) => setBrand((b) => ({ ...b, containerShadow: value }))}
          />
        </div>
        {!!brand.containerShadow && (
          <>
            <div class="flex flex-col gap-2">
              <Slider
                label="Shadow softness"
                value={brand.shadowSoftness}
                min={0}
                max={200}
                minLabel="Sharp"
                maxLabel="Soft"
                onChange={(value) => {
                  setBrand((b) => ({ ...b, shadowSoftness: isNaN(value) ? 0 : value }));
                }}
              />
            </div>
            <div class="flex flex-col gap-2">
              <Slider
                label="Shadow opacity"
                value={brand.shadowOpacity}
                min={0}
                max={100}
                minLabel="Transparent"
                maxLabel="Opaque"
                onChange={(value) => {
                  setBrand((b) => ({ ...b, shadowOpacity: isNaN(value) ? 0 : value }));
                }}
              />
            </div>
            <div class="flex flex-col gap-2">
              <StyledColorPicker
                name="shadowColor"
                color={brand.shadowColor || '#000000'}
                onPick={(shadowColor) => setBrand((s) => ({ ...s, shadowColor }))}
              >
                Shadow color
              </StyledColorPicker>
            </div>
          </>
        )}
      </div>
    </SettingsTab>
  );
}

function RootPane({ setEditMode }: SettingsPanelProps) {
  return (
    <div key="default" class="flex flex-col gap-4 an-fade-in-right">
      <h3 class="text-lg font-medium">Settings</h3>
      <div class="flex flex-col gap-2">
        <SettingsButton
          label="Background Settings"
          onClick={() => setEditMode('background')}
          icon={<IcoImage />}
        />
        <SettingsButton
          label="Background Accent"
          onClick={() => setEditMode('gradient')}
          icon={<IcoSwatch />}
        />
        <SettingsButton
          label="Logo Settings"
          onClick={() => setEditMode('logo')}
          icon={<IcoCube />}
        />
        <SettingsButton
          label="Container Settings"
          onClick={() => setEditMode('container')}
          icon={<IcoRectangleGroup />}
        />
        <SettingsButton
          label="Button & Link Settings"
          onClick={() => setEditMode('buttons')}
          icon={<IcoCursor />}
        />
      </div>
      <p class="text-sm text-gray-500 mt-4">
        Click on any part of the preview to edit its settings, or use the buttons above.
      </p>
    </div>
  );
}

export function SettingsPanel(props: SettingsPanelProps) {
  switch (props.editMode) {
    case 'background':
      return <BackgroundPane {...props} />;
    case 'logo':
      return <LogoPane {...props} />;
    case 'gradient':
      return <GradientPane {...props} />;
    case 'buttons':
      return <ButtonsPane {...props} />;
    case 'container':
      return <ContainerPane {...props} />;
    default:
      return <RootPane {...props} />;
  }
}
